/**
 * @file: SearchCriterionProviderBase.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { Observable } from 'rxjs';
import {
  SearchCriterionOption,
  SearchCriterionProvider,
  SearchCriterionType,
  SearchCriterionValueType,
} from './types';

export abstract class AbstractSearchCriterionProvider<
  T extends SearchCriterionType,
  U = SearchCriterionValueType<T>,
> implements SearchCriterionProvider<T, U>
{
  constructor(public readonly type: T) {}

  get required() {
    return false;
  }

  get defaultValue(): U | undefined {
    return undefined;
  }

  equals(x: U | undefined, y: U | undefined): boolean {
    return x === y;
  }

  getKey(value: U | undefined): string {
    if (value == null) return '';
    return this.internalGetKey(value);
  }

  getDisplayText(value: U | undefined): string {
    if (value == null) return '全部';
    return this.internalGetDisplayText(value);
  }

  getDisplayImage(_value: U | undefined): string | undefined {
    return undefined;
  }

  getOptions(): Observable<SearchCriterionOption<U>[]> {
    return new Observable(subscriber => {
      const options = this.internalGetOptions();
      if (this.required) {
        subscriber.next(options);
      } else {
        subscriber.next([{ value: undefined }, ...options]);
      }
      subscriber.complete();
    });
  }

  protected internalGetKey(value: U): string {
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  abstract getLabel(): string;

  protected abstract internalGetDisplayText(value: U): string;
  protected abstract internalGetOptions(): SearchCriterionOption<U>[];
}
